// 6軸スコアリングシステム（Phase 2）

export interface DetailedScore {
  freshness: number;      // 新規性（既視感の低さ）
  specificity: number;    // 具体性（固有名詞・数値）
  personaFit: number;     // アカウント整合性
  replyHook: number;      // 返信したくなる度
  readability: number;    // 読みやすさ
  risk: number;           // 炎上リスク（低いほど良い）
  total: number;          // 総合スコア
}

export interface ScoringWeights {
  freshness: number;
  specificity: number;
  personaFit: number;
  replyHook: number;
  readability: number;
  risk: number;
}

// デフォルト重み（ユーザーが調整可能）
export const DEFAULT_WEIGHTS: ScoringWeights = {
  freshness: 0.25,
  specificity: 0.20,
  personaFit: 0.20,
  replyHook: 0.15,
  readability: 0.15,
  risk: -0.05,  // マイナス重み
};

// 言語判定（日本語 or 英語）
function isJapanese(text: string): boolean {
  const jpChars = (text.match(/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/g) || []).length;
  return jpChars / text.length > 0.1;
}

// 既視感フィルタ用（日本語）
const CLICHE_PHRASES_JA = [
  '一番の贅沢', '痛感してる', '痛感する', '本当に大切',
  'いかがでしたか', 'ご紹介します', 'お伝えします',
  '皆さんは', '〜な気がします', '〜だと思います',
  '誰もが知っている', '言うまでもなく', '実は',
  '成功の秘訣', '大切なこと', '意外と知られていない', '知らない人が多い',
  '◯◯のコツ', 'プロが教える', '秘密を公開', '裏技',
  '人生が変わる', '劇的に変わる', '効果抜群', '即効性',
  'たった◯◯で', 'これだけで', '簡単に', 'すぐできる',
  '感動した', '泣いた', '号泣', '涙が止まらない', '心が震えた',
  '今日から変われる', '明日から本気', '人生のターニングポイント',
  '気づきを得た', '目から鱗', '価値観が変わった',
];

// 既視感フィルタ用（英語）
const CLICHE_PHRASES_EN = [
  'game changer', 'let that sink in', 'nobody talks about',
  'here\'s the thing', 'unpopular opinion', 'hot take',
  'this is huge', 'mind-blowing', 'thread', '1/',
  'I\'ll go first', 'you need to see this', 'nobody is ready',
  'broke vs woke', 'just dropped', 'let me explain',
  'the secret to', 'most people don\'t know', 'here\'s why',
  'changed my life', 'life-changing', 'you won\'t believe',
  'stop scrolling', 'read this twice', 'bookmark this',
  'a quick thread', 'this changed everything', 'huge if true',
  'say it louder', 'normalize this', 'it\'s giving',
  'no one is talking about', 'the truth about', 'wake up',
];

const HOOK_ENDINGS_JA = [
  'わかる人いる？', 'あるある？', '同士いる？',
  'これ自分だけ？', 'どう思う？', 'ない？'
];

const HOOK_ENDINGS_EN = [
  'anyone else?', 'thoughts?', 'am I wrong?',
  'right?', 'agree?', 'who else?',
  'what do you think?', 'change my mind',
];

// 固有名詞パターン（共通 + 言語別）
const PROPER_NOUN_PATTERNS_COMMON = [
  /[A-Z][a-z]+/g,
  /VSCode|GitHub|Twitter|YouTube|Google|Apple|Amazon|Microsoft|OpenAI|Claude|ChatGPT|Meta|Tesla|Netflix|Slack|Discord|Notion|Figma|Vercel|Next\.js|React|TypeScript|Python|JavaScript|Docker|Kubernetes|AWS|Azure|GCP/gi,
  /iPhone|iPad|Mac|MacBook|Apple Watch|AirPods|Android|Pixel|Galaxy|Surface|Kindle|Switch|PS5|Xbox/gi,
  /LINE|Instagram|TikTok|Facebook|Reddit|Discord|Slack|Zoom|Teams|Notion|Evernote|Trello|Asana/gi,
  /Netflix|Amazon Prime|Disney\+|Hulu|YouTube Premium|Spotify|Apple Music/gi,
];

const PROPER_NOUN_PATTERNS_JA = [
  /東京|大阪|京都|北海道|沖縄|渋谷|新宿|池袋|秋葉原|原宿|銀座|横浜|名古屋|福岡|札幌/g,
  /スタバ|スターバックス|マック|マクドナルド|モス|ケンタ|セブン|ファミマ|ローソン|コンビニ|駅|電車|バス|タクシー|新幹線|飛行機|空港/g,
  /ニコニコ|pixiv|note|Zenn|Qiita|メルカリ|楽天|PayPay|Suica|PASMO|LINE Music/gi,
  /今日|昨日|明日|今週|先週|来週|今月|先月|来月|今年|去年|来年|今朝|昨夜|さっき|ついさっき|たった今/g,
];

const TIME_WORDS_JA = ['昨日', '今日', '今', 'さっき', '先ほど', 'つい先ほど', '最近'];
const TIME_WORDS_EN = ['yesterday', 'today', 'right now', 'just now', 'earlier', 'recently', 'last night', 'this morning'];

export const scoringService = {
  /**
   * 6軸スコアリング
   */
  calculateDetailedScore(
    tweetText: string,
    stylePrompt: string,
    exampleTweets: string[],
    weights: ScoringWeights = DEFAULT_WEIGHTS
  ): DetailedScore {
    
    // 1. 新規性（Freshness）
    const freshness = this.calculateFreshness(tweetText, exampleTweets);
    
    // 2. 具体性（Specificity）
    const specificity = this.calculateSpecificity(tweetText);
    
    // 3. アカウント整合性（Persona Fit）
    const personaFit = this.calculatePersonaFit(tweetText, stylePrompt, exampleTweets);
    
    // 4. 返信フック（Reply Hook）
    const replyHook = this.calculateReplyHook(tweetText);
    
    // 5. 読みやすさ（Readability）
    const readability = this.calculateReadability(tweetText);
    
    // 6. リスク（Risk）
    const risk = this.calculateRisk(tweetText);
    
    // 総合スコア
    const total = 
      (freshness * weights.freshness) +
      (specificity * weights.specificity) +
      (personaFit * weights.personaFit) +
      (replyHook * weights.replyHook) +
      (readability * weights.readability) +
      (risk * weights.risk);
    
    return {
      freshness,
      specificity,
      personaFit,
      replyHook,
      readability,
      risk,
      total: Math.max(0, Math.min(1, total)),
    };
  },

  /**
   * 1. 新規性（既視感の低さ）
   */
  calculateFreshness(tweetText: string, recentTweets: string[]): number {
    let score = 1.0;
    
    // ありがちフレーズペナルティ
    const jp = isJapanese(tweetText);
    const clichePhrases = jp ? CLICHE_PHRASES_JA : CLICHE_PHRASES_EN;
    let clicheCount = 0;
    for (const phrase of clichePhrases) {
      if (tweetText.toLowerCase().includes(phrase)) {
        clicheCount++;
      }
    }
    score -= Math.min(0.4, clicheCount * 0.1);

    // 同じフック末尾の重複ペナルティ
    const hookEndings = jp ? HOOK_ENDINGS_JA : HOOK_ENDINGS_EN;
    let sameHookCount = 0;
    for (const hook of hookEndings) {
      if (tweetText.endsWith(hook)) {
        for (const recent of recentTweets.slice(0, 10)) {
          if (recent.endsWith(hook)) {
            sameHookCount++;
          }
        }
      }
    }
    
    if (sameHookCount >= 3) {
      score -= 0.3;
    } else if (sameHookCount >= 2) {
      score -= 0.2;
    } else if (sameHookCount === 1) {
      score -= 0.1;
    }
    
    // 文の構造が似てるかチェック（n-gram類似度）
    const similarity = this.calculateMaxSimilarity(tweetText, recentTweets.slice(0, 5));
    if (similarity > 0.7) {
      score -= 0.3; // 非常に似てる
    } else if (similarity > 0.5) {
      score -= 0.2; // まあまあ似てる
    } else if (similarity > 0.3) {
      score -= 0.1; // 少し似てる
    }
    
    // 文頭パターンの重複
    const firstChars = tweetText.substring(0, Math.min(15, tweetText.length));
    let similarStartCount = 0;
    for (const recent of recentTweets.slice(0, 5)) {
      const recentFirst = recent.substring(0, Math.min(15, recent.length));
      if (firstChars === recentFirst) {
        similarStartCount++;
      }
    }
    if (similarStartCount > 0) {
      score -= Math.min(0.3, similarStartCount * 0.15);
    }
    
    return Math.max(0, score);
  },
  
  /**
   * n-gram類似度計算（簡易版）
   */
  calculateMaxSimilarity(text: string, comparisons: string[]): number {
    if (comparisons.length === 0) return 0;
    
    const ngramSize = 3; // tri-gram
    const textNgrams = this.getNgrams(text, ngramSize);
    
    let maxSim = 0;
    for (const comparison of comparisons) {
      const compNgrams = this.getNgrams(comparison, ngramSize);
      const similarity = this.jaccardSimilarity(textNgrams, compNgrams);
      maxSim = Math.max(maxSim, similarity);
    }
    
    return maxSim;
  },
  
  /**
   * n-gram抽出
   */
  getNgrams(text: string, n: number): Set<string> {
    const ngrams = new Set<string>();
    for (let i = 0; i <= text.length - n; i++) {
      ngrams.add(text.substring(i, i + n));
    }
    return ngrams;
  },
  
  /**
   * Jaccard類似度
   */
  jaccardSimilarity(set1: Set<string>, set2: Set<string>): number {
    const arr1 = Array.from(set1);
    const arr2 = Array.from(set2);
    const intersection = new Set(arr1.filter(x => set2.has(x)));
    const union = new Set([...arr1, ...arr2]);
    
    if (union.size === 0) return 0;
    return intersection.size / union.size;
  },

  /**
   * 2. 具体性（固有名詞・数値）
   */
  calculateSpecificity(tweetText: string): number {
    let score = 0.3;  // ベース
    
    // 数字の有無
    const hasNumbers = /\d+/.test(tweetText);
    if (hasNumbers) {
      score += 0.25;
    }
    
    // 固有名詞っぽいものの検出
    const jp = isJapanese(tweetText);
    const patterns = [...PROPER_NOUN_PATTERNS_COMMON, ...(jp ? PROPER_NOUN_PATTERNS_JA : [])];
    let properNounCount = 0;
    for (const pattern of patterns) {
      const matches = tweetText.match(pattern);
      if (matches) {
        properNounCount += matches.length;
      }
    }
    
    if (properNounCount >= 3) {
      score += 0.3;
    } else if (properNounCount >= 2) {
      score += 0.2;
    } else if (properNounCount >= 1) {
      score += 0.1;
    }
    
    // 時間表現
    const timeWords = jp ? TIME_WORDS_JA : TIME_WORDS_EN;
    for (const word of timeWords) {
      if (tweetText.toLowerCase().includes(word)) {
        score += 0.15;
        break;
      }
    }
    
    return Math.min(1.0, score);
  },

  /**
   * 3. アカウント整合性（文体・トーン一致）
   */
  calculatePersonaFit(tweetText: string, stylePrompt: string, exampleTweets: string[]): number {
    let score = 0.5;
    
    // 絵文字の使用頻度
    const emojiCount = (tweetText.match(/[\u{1F300}-\u{1F9FF}]/gu) || []).length;
    const exampleAvgEmoji = this.getAverageEmojiCount(exampleTweets);
    
    if (Math.abs(emojiCount - exampleAvgEmoji) <= 1) {
      score += 0.15;
    } else if (Math.abs(emojiCount - exampleAvgEmoji) > 3) {
      score -= 0.1;
    }
    
    // 文の長さ
    const avgExampleLength = this.getAverageLength(exampleTweets);
    const lengthDiff = Math.abs(tweetText.length - avgExampleLength);
    
    if (lengthDiff <= 30) {
      score += 0.15;
    } else if (lengthDiff > 100) {
      score -= 0.1;
    }
    
    // 改行の使用
    const lineBreaks = (tweetText.match(/\n/g) || []).length;
    const exampleAvgLineBreaks = this.getAverageLineBreaks(exampleTweets);
    
    if (Math.abs(lineBreaks - exampleAvgLineBreaks) <= 1) {
      score += 0.1;
    }
    
    // 句読点の使い方
    const hasQuestionMark = tweetText.includes('？') || tweetText.includes('?');
    const exampleQuestionRate = this.getQuestionMarkRate(exampleTweets);
    
    if (hasQuestionMark && exampleQuestionRate > 0.3) {
      score += 0.1;
    } else if (!hasQuestionMark && exampleQuestionRate < 0.2) {
      score += 0.05;
    }
    
    return Math.min(1.0, score);
  },

  /**
   * 4. 返信フック（返信したくなる度）
   */
  calculateReplyHook(tweetText: string): number {
    let score = 0.3;
    const jp = isJapanese(tweetText);
    const lower = tweetText.toLowerCase();

    // 質問形式
    const questionCount = (tweetText.match(jp ? /？/g : /\?/g) || []).length;
    if (questionCount > 0) {
      score += Math.min(0.25, 0.15 + (questionCount - 1) * 0.05);
    }

    // 共感誘発ワード
    const empathyWords = jp
      ? ['わかる', 'あるある', 'よね', '同じ', '共感', 'わかりみ', 'それな', 'これな', 'ほんとそれ', 'まじそれ', '同感', '激しく同意', 'めっちゃわかる', '超わかる']
      : ['relatable', 'same', 'felt this', 'so true', 'this hits', 'fr fr', 'literally me', 'big mood', 'mood', 'real talk', 'preach', 'exactly'];
    let empathyCount = 0;
    for (const word of empathyWords) {
      if (lower.includes(word)) empathyCount++;
    }
    if (empathyCount > 0) {
      score += Math.min(0.2, 0.15 + (empathyCount - 1) * 0.025);
    }

    // 行動喚起
    const ctaWords = jp
      ? ['教えて', '聞きたい', 'どう思う', 'ある？', 'いる？', 'ない？', '試して', 'チェックして', '見て', '使って', 'やってみて', 'おすすめ教えて', '知ってる人', '詳しい人', '誰か', 'リプで教えて', 'コメントで', '引用RTで', 'RT希望']
      : ['tell me', 'let me know', 'what do you think', 'drop your', 'share your', 'reply with', 'tag someone', 'who else', 'try this', 'check this out', 'follow me', 'retweet', 'bookmark this'];
    let ctaCount = 0;
    for (const word of ctaWords) {
      if (lower.includes(word)) ctaCount++;
    }
    if (ctaCount > 0) {
      score += Math.min(0.25, 0.2 + (ctaCount - 1) * 0.025);
    }

    // 意外性・引っかかり
    const hookWords = jp
      ? ['え', 'まじ', 'やばい', 'ヤバ', 'は？', 'えぐい', 'うそ', '嘘', 'マジで', 'ガチで', '本当に', 'マジか', 'ちょ', 'おい', 'ねえ', 'あのさ', 'ちょっと待って', '信じられない', 'びっくり', '驚いた', '衝撃']
      : ['wait', 'hold on', 'no way', 'wtf', 'insane', 'wild', 'bruh', 'actually', 'plot twist', 'hear me out', 'not gonna lie', 'ngl', 'honestly', 'lowkey', 'i can\'t believe'];
    for (const word of hookWords) {
      if (lower.includes(word)) { score += 0.1; break; }
    }

    // 議論誘発
    const debateWords = jp
      ? ['だと思う', 'じゃない？', 'と思うんだけど', '個人的には', '異論は認める', '反論ある？', '意見聞かせて', '賛成？反対？']
      : ['unpopular opinion', 'hot take', 'change my mind', 'i think', 'controversial', 'am i wrong', 'debate me', 'disagree'];
    for (const word of debateWords) {
      if (lower.includes(word)) { score += 0.1; break; }
    }

    // 未完結感
    const cliffhangerWords = jp
      ? ['続きは', '結論は後で', '次回', 'つづく', 'to be continued', '詳しくは', 'リプ欄で', 'スレッドで', '長くなるから']
      : ['thread', 'more below', 'story time', 'let me explain', 'here\'s why', 'a thread', 'to be continued', 'stay tuned', 'part 1'];
    for (const word of cliffhangerWords) {
      if (lower.includes(word)) { score += 0.08; break; }
    }

    return Math.min(1.0, score);
  },

  /**
   * 5. 読みやすさ
   */
  calculateReadability(tweetText: string): number {
    let score = 0.5;
    const jp = isJapanese(tweetText);
    const length = tweetText.length;

    // 理想的な長さ
    if (jp) {
      if (length >= 100 && length <= 200) score += 0.3;
      else if (length >= 60 && length <= 240) score += 0.2;
      else if (length < 30) score -= 0.2;
      else if (length > 250) score -= 0.1;
    } else {
      // 英語: word count based (ideal 15-40 words for tweets)
      const wordCount = tweetText.split(/\s+/).filter(w => w).length;
      if (wordCount >= 15 && wordCount <= 40) score += 0.3;
      else if (wordCount >= 8 && wordCount <= 50) score += 0.2;
      else if (wordCount < 5) score -= 0.2;
      else if (wordCount > 55) score -= 0.1;
    }

    // 改行の適度な使用
    const lineBreaks = (tweetText.match(/\n/g) || []).length;
    if (lineBreaks >= 1 && lineBreaks <= 3) score += 0.15;
    else if (lineBreaks > 5) score -= 0.1;

    // 一文が長すぎないか
    const sentenceSplitter = jp ? /[。！？\n]/ : /[.!?\n]/;
    const sentences = tweetText.split(sentenceSplitter).filter(s => s.trim());
    if (sentences.length > 0) {
      const avgLen = jp
        ? sentences.reduce((sum, s) => sum + s.length, 0) / sentences.length
        : sentences.reduce((sum, s) => sum + s.split(/\s+/).length, 0) / sentences.length;

      if (jp) {
        if (avgLen <= 50) score += 0.1;
        else if (avgLen > 100) score -= 0.15;
      } else {
        if (avgLen <= 20) score += 0.1;
        else if (avgLen > 35) score -= 0.15;
      }

      if (sentences.length >= 2 && sentences.length <= 4) score += 0.05;
    }

    // 絵文字の使用
    const emojiCount = (tweetText.match(/[\u{1F300}-\u{1F9FF}]/gu) || []).length;
    if (emojiCount >= 1 && emojiCount <= 3) score += 0.05;
    else if (emojiCount > 5) score -= 0.1;

    // 空白の使用
    if (/\s{2,}/.test(tweetText)) score += 0.05;

    // 日本語限定: かな漢字バランス
    if (jp) {
      const kanaCount = (tweetText.match(/[\u3040-\u309F\u30A0-\u30FF]/g) || []).length;
      const kanjiCount = (tweetText.match(/[\u4E00-\u9FFF]/g) || []).length;
      if (kanaCount / length > 0.8) score -= 0.05;
      if (kanjiCount / length > 0.5) score -= 0.05;
    } else {
      // 英語: ALL CAPS penalty
      const capsRatio = (tweetText.match(/[A-Z]/g) || []).length / length;
      if (capsRatio > 0.5 && length > 20) score -= 0.1;
    }

    return Math.min(1.0, score);
  },

  /**
   * 6. リスク（炎上・センシティブ）
   */
  calculateRisk(tweetText: string): number {
    let risk = 0.0;
    const jp = isJapanese(tweetText);
    const lower = tweetText.toLowerCase();

    let highRiskCount = 0;
    let mediumRiskCount = 0;

    // 高リスクワード
    const highRiskWords = jp
      ? ['死ね', '殺す', '殺したい', '消えろ', 'ぶっ殺', '首吊', '自殺', '障害者', '池沼', 'ガイジ', 'キチガイ', '気狂い']
      : ['kys', 'kill yourself', 'go die', 'neck yourself'];
    for (const word of highRiskWords) {
      if ((jp ? tweetText : lower).includes(word)) highRiskCount++;
    }

    // 中リスクワード
    const mediumRiskWords = jp
      ? ['バカ', 'クソ', 'ゴミ', 'カス', 'クズ', '底辺', '低学歴', 'まんさん', 'ちんさん', 'BBA', 'ババア', 'ジジイ', 'クソガキ']
      : ['stfu', 'dumb', 'idiot', 'moron', 'loser', 'trash'];
    for (const word of mediumRiskWords) {
      if ((jp ? tweetText : lower).includes(word)) mediumRiskCount++;
    }

    risk += highRiskCount * 0.5;
    risk += mediumRiskCount * 0.2;

    // 過激な感情表現
    const exclPattern = jp ? /！{3,}/g : /!{3,}/g;
    const exclamationMatches = tweetText.match(exclPattern);
    if (exclamationMatches) {
      risk += Math.min(0.3, exclamationMatches.length * 0.1);
    }

    // CAPS LOCK感
    if (jp) {
      const capsMatches = tweetText.match(/[Ａ-Ｚ]{3,}/g);
      if (capsMatches) risk += Math.min(0.15, capsMatches.length * 0.05);
    } else {
      const words = tweetText.split(/\s+/);
      const capsWords = words.filter(w => w.length > 2 && w === w.toUpperCase() && /[A-Z]/.test(w));
      if (capsWords.length >= 3) risk += Math.min(0.15, capsWords.length * 0.03);
    }

    // 政治・宗教系キーワード
    const sensitiveTopics = jp
      ? {
          politics: ['政治', '選挙', '政権', '野党', '与党', '国会', '議員', '総理', '首相'],
          religion: ['宗教', '神', '仏', '信仰', '宗派', '教会', '寺', '神社'],
          discrimination: ['差別', '人種', '民族', 'ヘイト', '偏見', '蔑視'],
          gender: ['フェミ', 'ミサンドリ', 'ミソジニ', '男女平等', 'LGBT'],
          conflict: ['戦争', '紛争', '侵略', '占領', '軍事', '核'],
        }
      : {
          politics: ['democrat', 'republican', 'maga', 'woke', 'liberal', 'conservative', 'trump', 'biden'],
          religion: ['religion', 'atheist', 'christian', 'muslim', 'jewish'],
          discrimination: ['racist', 'sexist', 'bigot', 'hate speech'],
          gender: ['feminism', 'misogyny', 'misandry', 'incel', 'lgbtq'],
          conflict: ['war', 'invasion', 'genocide', 'nuclear'],
        };

    for (const [, words] of Object.entries(sensitiveTopics)) {
      for (const word of words) {
        if ((jp ? tweetText : lower).includes(word)) {
          risk += 0.1;
          break;
        }
      }
    }

    // デマ・誤情報フレーズ
    const misinfoWords = jp
      ? ['絶対効く', '100%治る', '必ず儲かる', '確実に痩せる', '医者が隠す', 'マスコミが報じない', '真実を知れ', '拡散希望', '政府の陰謀', '闇', '裏側', '本当の理由']
      : ['guaranteed results', '100% cure', 'doctors don\'t want you', 'media won\'t show', 'they don\'t want you to know', 'wake up sheeple', 'do your research', 'conspiracy', 'big pharma', 'deep state'];
    for (const word of misinfoWords) {
      if ((jp ? tweetText : lower).includes(word)) {
        risk += 0.15;
        break;
      }
    }

    // 個人情報
    const hasPhoneNumber = /\d{2,4}-\d{2,4}-\d{4}/.test(tweetText) || /\(\d{3}\)\s?\d{3}-\d{4}/.test(tweetText);
    const hasAddress = jp
      ? /[都道府県市区町村]{2,}.*\d+[-丁目番地号]/.test(tweetText)
      : /\d+\s+\w+\s+(street|st|ave|avenue|blvd|road|rd|drive|dr)\b/i.test(tweetText);
    if (hasPhoneNumber || hasAddress) risk += 0.3;

    // 怪しいドメイン
    const suspiciousDomains = ['.xyz', '.tk', '.ml', '.ga', '.cf', 'bit.ly', 'tinyurl'];
    for (const domain of suspiciousDomains) {
      if (lower.includes(domain)) { risk += 0.1; break; }
    }

    return Math.min(1.0, risk);
  },

  // ユーティリティ関数
  getAverageEmojiCount(tweets: string[]): number {
    if (tweets.length === 0) return 0;
    const total = tweets.reduce((sum, tweet) => {
      return sum + (tweet.match(/[\u{1F300}-\u{1F9FF}]/gu) || []).length;
    }, 0);
    return total / tweets.length;
  },

  getAverageLength(tweets: string[]): number {
    if (tweets.length === 0) return 150;
    const total = tweets.reduce((sum, tweet) => sum + tweet.length, 0);
    return total / tweets.length;
  },

  getAverageLineBreaks(tweets: string[]): number {
    if (tweets.length === 0) return 0;
    const total = tweets.reduce((sum, tweet) => {
      return sum + (tweet.match(/\n/g) || []).length;
    }, 0);
    return total / tweets.length;
  },

  getQuestionMarkRate(tweets: string[]): number {
    if (tweets.length === 0) return 0;
    const withQuestion = tweets.filter(t => t.includes('？') || t.includes('?')).length;
    return withQuestion / tweets.length;
  },

  /**
   * 重みをカスタマイズして保存
   */
  saveWeights(weights: ScoringWeights): void {
    localStorage.setItem('scoring_weights', JSON.stringify(weights));
  },

  /**
   * 保存された重みを取得
   */
  loadWeights(): ScoringWeights {
    try {
      const saved = localStorage.getItem('scoring_weights');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      // ignore
    }
    return DEFAULT_WEIGHTS;
  },
};
